import React, { useRef, useState } from 'react';
import { Box, ListItemButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../store/useUserStore';
import UserAvatar from './UserAvatar';
import { COLLAPSED_ITEM_SX, SIDEBAR_FOCUS_SX } from './SidebarFinanceGroup';

import { palette } from '../theme';
/** The role, said in the interface language. */
export const useUserRoleLabel = (): string => {
  const { t } = useTranslation();
  const userInfo = useUserStore(state => state.userInfo);
  if (userInfo?.isAdmin) return t('roles.admin');
  if (userInfo?.isSuperAdmin) return t('roles.superAdmin');
  if (userInfo?.isKindergartenAdmin) return t('roles.kindergartenAdmin');
  return '';
};

/**
 * Who is signed in, at the foot of the sidebar, with Logout behind it.
 *
 * Logout used to be a row of its own, one stray click from ending the session. In a menu it
 * takes a deliberate second step, and the row above it can say whose session it is.
 *
 * Collapsed, the row shrinks to the avatar and the same menu opens to its right.
 */
const SidebarAccount: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const { logout } = useAuth0();
  const user = useUserStore(state => state.userInfo?.user);
  const role = useUserRoleLabel();
  const rowRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const label = t('nav.account', { name: fullName });

  const close = () => {
    setOpen(false);
    // back to the row, so keyboard users are not dropped at the top of the page
    rowRef.current?.focus();
  };

  const row = collapsed ? (
    <Tooltip title={role ? `${fullName} · ${role}` : fullName} placement="right">
      <ListItemButton
        ref={rowRef}
        onClick={() => setOpen(previous => !previous)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        sx={{ ...COLLAPSED_ITEM_SX, ...(open && { backgroundColor: palette.sidebar.selected }) }}
      >
        <UserAvatar user={user} />
      </ListItemButton>
    </Tooltip>
  ) : (
    <ListItemButton
      ref={rowRef}
      onClick={() => setOpen(previous => !previous)}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={label}
      sx={{
        height: 56,
        p: '0 12px 0 8px',
        mb: 0,
        gap: '10px',
        ...(open && { backgroundColor: palette.sidebar.selected }),
        '&:hover': { backgroundColor: palette.sidebar.selected },
        ...SIDEBAR_FOCUS_SX
      }}
    >
      <UserAvatar user={user} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: 'white' }}>
          {fullName}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: palette.sidebar.textMuted }}>
          {role}
        </Typography>
      </Box>
      <ExpandLessIcon
        sx={{ fontSize: 20, color: palette.sidebar.textMuted, transition: 'transform 150ms ease', transform: open ? 'rotate(180deg)' : 'none' }}
      />
    </ListItemButton>
  );

  return (
    <>
      {row}
      <Menu
        anchorEl={rowRef.current}
        open={open}
        onClose={close}
        anchorOrigin={collapsed ? { vertical: 'bottom', horizontal: 'right' } : { vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { elevation: 3, sx: { width: 232, borderRadius: '10px', ...(collapsed ? { ml: 1 } : { mb: 1 }) } },
          list: { sx: { py: 1 }, 'aria-label': label }
        }}
      >
        {/* who this is: a heading, not an option */}
        <Box
          component="li"
          role="presentation"
          sx={{ p: '8px 16px 12px', mb: 1, borderBottom: `1px solid ${palette.line}`, listStyle: 'none', minWidth: 0 }}
        >
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: palette.text.primary }}>
            {fullName}
          </Typography>
          {user?.email && (
            <Typography noWrap sx={{ fontSize: 12, color: palette.text.secondary }}>
              {user.email}
            </Typography>
          )}
          {role && <Typography sx={{ fontSize: 12, color: palette.text.secondary }}>{role}</Typography>}
        </Box>
        <MenuItem
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          sx={{ height: 44, px: 2, gap: 1.5, fontSize: 16, color: palette.text.primary, '&:hover': { backgroundColor: palette.surface.hover } }}
        >
          <LogoutOutlinedIcon sx={{ fontSize: 20, color: palette.text.secondary }} />
          {t('nav.logout')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default SidebarAccount;
